import * as React from 'react';
import { observable, action } from 'mobx';
import { Helper } from '../../../Helper';
import { GroupListItem, GroupList, GroupListColumnType, GroupListGroup } from '../../../interfaces/IGroupList';
import { ITargetingGroup } from '../../../interfaces/ITargetingGroup';
import { ITargetingGroupValues } from '../../../interfaces/ITargetingGroupValues';
import { ITargetingProperties } from '../../../interfaces/ITargetingProperties';
import { ITargetTerm } from '../../../interfaces/ITargetTerm';
import { IAIcon } from '../../icon/Icon';
import { LocalizationService } from '../../../services/LocalizationService';
import { EWizardStep } from '../Constants';
import { IAccordion } from '../../../interfaces/IAccordion';
import { IExtendedUserPermission } from '../../../interfaces/IUserPermission';
import { IUser } from '../../../interfaces/IUser';
import { ITargetingExistingStrucuture } from '../../../interfaces/ITargetingExistingStructure';
import { IApplication } from '../../../interfaces/IApplication';
import { IInstance } from '../../../interfaces/IInstance';
import { IHubSite } from '../../../interfaces/IHubSite';
import { IOption } from '../../../interfaces/IOption';
import { IDocumentLibrary } from '../../../interfaces/IDocumentLibrary';
export class ContentOrganizerStore {

  // Props
  @observable public component: string;
  @observable public applicationName: "Messages" | "Navigator" | "Toolbox" | "Events" | "Content Organizer";
  @observable public width: number;
  @observable public isMobile: boolean;
  @observable public title: string;
  @observable public token: string;
  @observable public tenant: string;
  @observable public environment: "Development" | "Test" | "Production";
  @observable public highlightColor: string = "#025159";
  @observable public language: "Danish" | "English" | "Swedish" | "German" | "Portuguese";
  @observable public isEditMode: boolean;
  @observable public instance: string;

  // Progress Bar
  @observable public selectedStep: EWizardStep = EWizardStep.CreateContentOrganizer;
  @observable public lastStepIndex: number; // 

  // Create Panel
  @observable public selectedTemplate: string;
  @observable public selectedNavigatorInstance: IInstance;
  @observable public selectedHubSite: IHubSite;
  @observable public contentOrganizerTitle: string;
  @observable public siteCollectionAddress: string;
  @observable public siteCollectionAddressNameAvailable: boolean;
  @observable public siteCollectionAddressNameHasSpecialCharacters: boolean;
  @observable public contentOrganizerDescription: string;
  @observable public selectedLanguage: string;
  @observable public siteOwners: IUser[];
  @observable public selectedTimeZone: string;
  @observable public newSiteCollection: boolean;
  @observable public selectedSiteItem: any;
  @observable public navigatorInstances: IInstance[];
  @observable public instanceOptions: IOption[] = [];
  @observable public hubSiteOptions: IOption[] = [];
  @observable public hubSiteList: IHubSite[];
  @observable public selectedFeaturedImageUrl: string;
  @observable public isExistingContentOrganizerInstance: boolean;
  @observable public hideHubMenu: boolean = true;
  @observable public hideLocalMenu: boolean;

  // Config Panel
  @observable public requireContentApproval: boolean;
  @observable public approvalList: IUser[];
  @observable public useMetadata: boolean;
  @observable public isUseMetadataRequired: boolean;
  @observable public termList: string[];
  @observable public termSetId: string;
  @observable public useRevisionDate: boolean;
  @observable public revisioDateDays: number;
  @observable public isUseRevisionDateRequired: boolean;
  @observable public usePageResponsible: boolean;
  @observable public isUsePageReesponsibleRequired: boolean;
  @observable public useNavigator: boolean;
  @observable public useHubSite: boolean;
  @observable public documentLibraries: IDocumentLibrary[];

  // Targeting & Permissions Panel
  @observable public permissionItems: IAccordion[];
  @observable public userToAdd: IExtendedUserPermission;
  @observable public usePermissions: boolean;
  @observable public inheritAppPermissions: boolean;
  @observable public inheritSitePermissions: boolean;
  @observable public permissions: IExtendedUserPermission[];

  @observable public id: string = Helper.getRandomStringKey();
  @observable public loading: boolean;
  @observable public groupListItem: GroupListItem[];
  @observable public groupedList: GroupList;
  @observable public groups: GroupListGroup[];
  @observable public groupsList: ITargetingGroup[];
  @observable public selectedUserGroup: ITargetingGroup;
  @observable public userGroupToEdit: ITargetingGroup;
  @observable public userGroupToDelete: ITargetingGroup;
  @observable public originalUserGroupToEdit: ITargetingGroup;
  @observable public userGroupIndex: number;

  @observable public targetingProperties: ITargetingProperties[];
  @observable public termsList: ITargetTerm[];
  @observable public userPropertiesList: string[];

  // Saving targeting options
  @observable public radioValue: string;
  @observable public color: string;
  @observable public storedValues: Array<{ label: string, id: string, value?: boolean }>;
  @observable public allValues: boolean;
  @observable public useDefaultValues: boolean;
  @observable public enableTargeting: boolean;
  @observable public treeValues: Array<{ label: string, id: string, field?: string }>;
  @observable public isSavingTarget: boolean;
  @observable public required: boolean;
  @observable public targetingExistingStructure: ITargetingExistingStrucuture;
  @observable public selectedApplication: IApplication;
  @observable public selectedValues: any[];

  @observable public instancesList: { [key: string]: Array<{ id?: number, title: string, instance: string }> };
  @observable public applicationsData: IApplication[];

  @observable public isLoadingProfileData: boolean;
  @observable public isLoadingApplication: boolean;

  @observable public hideProfileInformation: boolean = true;

  @action public setStep(step: EWizardStep): void {
    if (step > this.lastStepIndex) { return; }
    this.selectedStep = step;
  }

  @action public checkData(): boolean {
    if (this.newSiteCollection) {
      if (!this.contentOrganizerTitle) {
        return true;
      }
      if (!this.siteCollectionAddress) {
        return true;
      }
      if (!this.siteCollectionAddressNameAvailable) {
        return true;
      }
      if (this.siteCollectionAddressNameHasSpecialCharacters) {
        return true;
      }
      if (!this.selectedLanguage) {
        return true;
      }
      if (!this.siteOwners) {
        return true;
      }
      if (this.siteOwners && this.siteOwners.length === 0) {
        return true;
      }
      return false;
    } else {
      if (!this.siteCollectionAddress) {
        return true;
      }
      return false;
    }
  }

  @action public clearData(): void {
    this.selectedStep = 0;
    // this.selectedTemplate;
    this.contentOrganizerTitle = undefined;
    this.siteCollectionAddress = undefined;
    this.siteCollectionAddressNameAvailable = false;
    this.siteCollectionAddressNameHasSpecialCharacters = false;
    this.contentOrganizerDescription = undefined;
    this.siteOwners = [];
    this.selectedTimeZone = "3";
    this.newSiteCollection = undefined;

    this.requireContentApproval = undefined;
    this.approvalList = undefined;
    this.useMetadata = undefined;
    this.isUseMetadataRequired = undefined;
    this.termList = undefined;
    this.termSetId = undefined;
    this.useRevisionDate = undefined;
    this.revisioDateDays = undefined;
    this.isUseRevisionDateRequired = undefined;
    this.usePageResponsible = undefined;
    this.isUsePageReesponsibleRequired = undefined;
    this.documentLibraries = [];

    this.targetingProperties = undefined;
    this.termsList = undefined;
    this.userPropertiesList = undefined;
    this.permissionItems = undefined;
    this.userToAdd = undefined;
    this.usePermissions = undefined;
    this.permissions = [];
  }

  //////////////////////////////////////////////////////////////

  @action public updateUserProperties(list: string[]) {
    this.userPropertiesList = list;
  }

  @action public updateTerms(list: ITargetTerm[]) {
    this.termsList = list;
  }

  @action public updatePropertiesList(list: ITargetingProperties[]) {
    this.targetingProperties = list;
  }


  // Language

  @action public setLanguage(language: "Danish" | "English" | "Swedish" | "German" | "Portuguese") {
    this.language = language;
    Helper.setLocalStorage("IA_language", language);

  }

  @action public getGroups(): void {
    const columnsTitle = [{ title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupLabel, name: "Title" }, { title: "", name: "Edit" }, { title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupValues, name: "Values" }];
    const items = this.groupListItem;
    this.groups = [{
      name: "group1",
      expanded: true,
      title: "",
      items,
      hideTitle: true,
      borderEveryItem: true
    }];

    this.groupedList = {
      columns: columnsTitle,
      groups: this.groups,
      hideFirstColumn: true
    };
  }

  // Saving

  @observable public isSaving: boolean;
  @action public transformDataToGroupList = (groups: ITargetingGroup[]) => {
    this.groupsList = groups.sort((g1, g2) => {
      if (g1.title.toLocaleLowerCase() > g2.title.toLocaleLowerCase()) {
        return 1;
      }
      if (g1.title.toLocaleLowerCase() < g2.title.toLocaleLowerCase()) {
        return -1;
      }
      return 0;
    });
    const items: GroupListItem[] = [];
    groups.forEach(x => {

      const item = {
        title: x.title,
        id: x.id,
        name: x.title,
        columns: []
      };

      item.columns.push({
        field: "Title",
        title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupLabel,
        type: GroupListColumnType.Text,
        value: (
          <div style={{ position: 'relative', top: '3px' }}>
            <div style={{ float: 'left', position: 'relative', top: '5px' }}>
              <IAIcon
                title={"People"}
                color={this.highlightColor}
                size={17} />
            </div>
            <div style={{ float: 'left', position: 'relative' }}>{x.title}</div>
          </div>
        )
      });

      item.columns.push({
        field: "Edit",
        title: "",
        type: GroupListColumnType.Icon,
        value: true
      });

      let values: ITargetingGroupValues[];
      let valueNames = "";

      if (x.values) {
        values = JSON.parse(x.values) as ITargetingGroupValues[];
        values.forEach((_) => {
          valueNames += _.displayName + "; ";
        });
      }

      item.columns.push({
        field: "Values",
        title: new LocalizationService().strings.TargetingAdmin_UserGroup_GroupValues,
        type: GroupListColumnType.Text,
        value: valueNames
      });

      items.push(item);

    });

    this.groupListItem = items;
    this.getGroups();

  }

  // Popup
  @observable public showCancelEditPopup: boolean;
}